import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';

import { Multiselect } from 'react-widgets';
import { makeStyles } from '@material-ui/core/styles';
import { TextField } from '@material-ui/core';

import PrimaryButton from './lib/buttons/PrimaryButton';
import SecondaryButton from './lib/buttons/SecondaryButton';
import NeutralButton from './lib/buttons/NeutralButton';
import { Loading } from '../stories/Loading';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  invoiceContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
  invoiceDate: {
    width: 160,
    marginTop: '12px',
    alignSelf: 'center',
    '& input': {
      color: '#c23616',
      textAlign: 'center',
    },
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
    marginTop: '12px',
  },
  guestInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {},
    [theme.breakpoints.up('sm')]: {
      marginLeft: '56px',
    },
  },
  footerButtons: {
    display: 'flex',
    marginTop: '24px',
    marginBottom: '12px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('md')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-around',
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
  button: {
    width: '100%',
    maxWidth: '300px',
    marginTop: '24px',
    alignSelf: 'center',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

interface InvoiceProps {}

const Invoice = (props: InvoiceProps) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [invoiceId, setInvoiceId] = useState<string>();
  const [invoiceDetails, setInvoiceDetails] = useState<any>();
  const [newFirstName, setNewFirstName] = useState<string>();
  const [newLastName, setNewLastName] = useState<string>();
  const [newAddress, setNewAddress] = useState<string>();
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>();
  const [newGstNumber, setNewGstNumber] = useState<string>();

  const [companyName, setCompanyName] = useState<any>();
  const [gstMappings, setGstMappings] = useState<any[]>();

  useEffect(() => reset(), [invoiceId]);

  const reset = () => {
    setInvoiceDetails(undefined);
    setNewFirstName(undefined);
    setNewLastName(undefined);
    setNewAddress(undefined);
    setNewPhoneNumber(undefined);
    setNewGstNumber(undefined);
  };

  const fetchInvoiceDetails = () => {
    if (invoiceId && invoiceId.length > 4) {
      setLoading(true);
      axios
        .get(`/api/invoice?id=${invoiceId}&fetchOnly=${true}`)
        .then((res) => res.data)
        .then((res) => {
          if (res.errno > 0) {
            alert('Error: ' + res.msg);
          } else {
            // console.log(res);
            setInvoiceDetails(res);
          }
        })
        .catch((err) => {
          console.log(err);
          alert('Failed to fetch bill info');
        })
        .finally(() => setLoading(false));
    } else {
      alert('Invalid Invoice No');
    }
  };

  useEffect(() => getAllGstMappings(), []);

  const getAllGstMappings = () => {
    axios.get('/api/fetchGstMappings').then(({ data }) => {
      setGstMappings(data);
    });
  };

  const modifyBill = () => {
    if (
      newFirstName ||
      newLastName ||
      newAddress ||
      newPhoneNumber ||
      newGstNumber
    ) {
      const newDetails = {
        invoiceId: invoiceId,
        newFirstName: newFirstName,
        newLastName: newLastName,
        newAddress: newAddress,
        newContact: newPhoneNumber,
        newGstNumber: newGstNumber,
      };

      setLoading(true);

      axios
        .post('/api/modifyBill', newDetails)
        .then((res) => res.data)
        .then((mRes) => {
          if (mRes.errno > 0) {
            alert('Error: ' + mRes.msg);
          } else {
            axios
              .get(`/api/invoice?id=${mRes.id}`, { responseType: 'blob' })
              .then((res) => res.data)
              .then((blob) => {
                var file = new Blob([blob], { type: 'application/pdf' });
                var url = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Bill-${mRes.id}`);
                document.body.appendChild(link);
                link.click();
                link?.parentNode?.removeChild(link);
              })
              .catch((err) => {
                alert(err.response.data);
              });
          }
        })
        .catch((err) => {
          alert(err.response.data);
        })
        .finally(() => {
          setLoading(false);
          reset();
        });
    } else {
      alert('No Changes');
    }
  };

  const showInvoiceModifier = () => {
    if (invoiceDetails) {
      const {
        _first_name,
        _last_name,
        _address,
        _contact,
        _gst_number,
        _invoice_date,
        irn_status,
      } = invoiceDetails;

      let invoiceMDate = moment(_invoice_date).local();
      let presentMDate = moment().local();

      let invoiceDate = invoiceMDate.format('DD-MM-YYYY');
      let dayCount = presentMDate.diff(invoiceMDate, 'days');
      let modifyDisabled = dayCount > 2 || irn_status === 'Complete';
      let filteredGstMappings = gstMappings;
      let hideGstModifier = false;

      if (_gst_number && _gst_number.trim().length === 15) {
        filteredGstMappings = gstMappings?.filter(
          (item) => item.gstin !== _gst_number
        );
        filteredGstMappings?.unshift({
          location: '',
          state_code: '',
          tenant_id: '',
          trade_name: '',
          state: 'Remove GST',
          gstin: 'NONE',
          legal_name: 'NONE',
        });
      } else {
        hideGstModifier = true;
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <hr className={classes.separatorLine} style={{ marginTop: '36px' }} />
          <TextField
            className={classes.invoiceDate}
            disabled
            value={`Date: ${invoiceDate || ''} (${dayCount})`}
          />
          <div className={classes.mainContainer}>
            <div className={classes.guestInfoContainer}>
              <TextField
                label="First Name"
                variant="outlined"
                disabled
                value={_first_name || ''}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                disabled
                value={_last_name || ''}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="Address"
                variant="outlined"
                disabled
                value={_address || ''}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="Phone"
                variant="outlined"
                disabled
                value={_contact || ''}
                style={{ width: 300, marginTop: '24px' }}
              />
              {hideGstModifier ? (
                []
              ) : (
                <TextField
                  label="GST"
                  variant="outlined"
                  disabled
                  value={_gst_number || ''}
                  style={{ width: 300, marginTop: '24px' }}
                />
              )}
            </div>
            <div className={classes.formContainer}>
              <TextField
                label="New First Name"
                variant="outlined"
                disabled={modifyDisabled}
                value={newFirstName || ''}
                onChange={(e) => setNewFirstName(e.target.value)}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="New Last Name"
                variant="outlined"
                disabled={modifyDisabled}
                value={newLastName || ''}
                onChange={(e) => setNewLastName(e.target.value)}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="New Address"
                variant="outlined"
                disabled={
                  modifyDisabled ||
                  (newGstNumber !== undefined && newGstNumber !== 'NONE')
                }
                value={newAddress || ''}
                onChange={(e) => setNewAddress(e.target.value)}
                style={{ width: 300, marginTop: '24px' }}
              />
              <TextField
                label="New Phone No"
                variant="outlined"
                disabled={modifyDisabled}
                value={newPhoneNumber || ''}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                style={{ width: 300, marginTop: '24px' }}
              />
              {hideGstModifier
                ? []
                : [
                    <TextField
                      key="1"
                      label="New GST No"
                      variant="outlined"
                      value={newGstNumber || ''}
                      style={{ width: 300, marginTop: '24px' }}
                    />,
                    <Multiselect
                      key="2"
                      placeholder="Company Name"
                      style={{ width: 300, marginTop: '24px' }}
                      data={filteredGstMappings}
                      groupBy={(info) => info.state}
                      textField={(info: any) => info.legal_name}
                      value={companyName}
                      onChange={(r) => {
                        if (r.length === 1) {
                          setCompanyName(r);
                          setNewGstNumber(r[0].gstin);
                          setNewAddress(r[0].legal_name);
                        } else if (r.length === 0) {
                          setCompanyName([]);
                          setNewGstNumber(undefined);
                          setNewAddress(undefined);
                        }
                      }}
                      renderListGroup={({ group }) => {
                        return (
                          <span
                            style={{
                              color: '#ff1757',
                              fontWeight: 600,
                              textDecoration: 'underline',
                            }}
                          >
                            {group}
                          </span>
                        );
                      }}
                      renderTagValue={({ item }) => {
                        return (
                          <span
                            style={{
                              color: 'black',
                              fontWeight: 400,
                            }}
                          >
                            {item.legal_name}
                          </span>
                        );
                      }}
                      renderListItem={({ item }) => {
                        return (
                          <span
                            style={{
                              color: 'black',
                              fontWeight: 400,
                              fontSize: '14px',
                            }}
                          >
                            {item.legal_name}
                          </span>
                        );
                      }}
                    />,
                  ]}
            </div>
          </div>
          <div className={classes.footerButtons}>
            <SecondaryButton
              onClick={() => setInvoiceId(undefined)}
              className={classes.button}
            >
              Reset
            </SecondaryButton>
            <NeutralButton
              onClick={() => {
                axios
                  .get(`/api/invoice?id=${invoiceId}`, {
                    responseType: 'blob',
                  })
                  .then((res) => res.data)
                  .then((blob) => {
                    var file = new Blob([blob], {
                      type: 'application/pdf',
                    });
                    var url = URL.createObjectURL(file);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Bill-${invoiceId}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link?.parentNode?.removeChild(link);
                  })
                  .catch((err) => {
                    alert(err);
                  });
              }}
              className={classes.button}
            >
              Download
            </NeutralButton>
            <PrimaryButton
              onClick={() => {
                if (modifyBill) {
                  modifyBill();
                } else {
                  alert('Bills older than 15 days cannot be modified!');
                }
              }}
              className={classes.button}
            >
              Modify
            </PrimaryButton>
          </div>
        </div>
      );
    } else {
      return '';
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Invoice</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.invoiceContainer}>
          <TextField
            label="Invoice No"
            variant="outlined"
            placeholder="Ex: 1-21/22"
            value={invoiceId || ''}
            onChange={(e) => setInvoiceId(e.target.value)}
            style={{ width: 300, marginTop: '32px' }}
          />
          <NeutralButton
            style={{ width: 300, marginTop: '32px' }}
            onClick={() => fetchInvoiceDetails()}
          >
            Go
          </NeutralButton>
        </div>
        {showInvoiceModifier()}
      </div>
    );
  }
};

export default Invoice;
